import { ThemeProvider as EmotionThemeProvider, Global, useTheme } from '@emotion/react';
import { ThemeProvider as MuiThemeProvider, useMediaQuery } from '@mui/material';
import { IconContext } from '@phosphor-icons/react';
import { emotionDarkTheme, emotionLightTheme } from '@shared/constants/EmotionTheme';
import { muiDarkTheme, muiLightTheme } from '@shared/constants/MuiTheme';
import { APP_SETTINGS } from '@shared/constants/StorageValues';
import { color } from '@shared/constants/Styles';
import LocalAppSettings, { PanelOrientation } from '@shared/models/users/LocalAppSettings';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

interface ThemeContextType {
    theme: 'light' | 'dark';
    spacing: 'compact' | 'comfortable' | 'spacious';
    setSpacing: (spacing: 'compact' | 'comfortable' | 'spacious') => void;
    scaleSize: number;
    setScaleSize: (size: number) => void;
    panelButtonScaleSize: number;
    setPanelButtonScaleSize: (size: number) => void;
    setThemeDirectly: (theme: 'light' | 'dark') => void;
    panelPosition: 'left' | 'right' | 'top' | 'bottom';
    setPanelPosition: (position: 'left' | 'right' | 'top' | 'bottom') => void;
}

const defaultThemeContext: ThemeContextType = {
    theme: 'light',
    setThemeDirectly: () => {},
    spacing: 'comfortable',
    setSpacing: () => {},
    scaleSize: 16,
    setScaleSize: () => {},
    panelButtonScaleSize: 16,
    setPanelButtonScaleSize: () => {},
    panelPosition: 'right',
    setPanelPosition: () => {},
};

export const ThemeContext = createContext<ThemeContextType>(defaultThemeContext);

export const GlobalStyles = () => {
    const theme = useTheme();
    return (
        <Global
            styles={{
                html: {
                    fontSize: `${theme.scaleSize}px`,
                },
                body: {
                    color: theme.color.text,
                    ':not(:has(#command-overlay, #panel-overlay))': {
                        backgroundColor: theme.color.background,
                    },
                },
                // for storybook
                '.light-theme': {
                    color: color.primary_dark,
                    backgroundColor: color.bg_light,
                },
                '.dark-theme': {
                    color: color.bg_light,
                    backgroundColor: color.primary_intense,
                },
            }}
        />
    );
};

export const ThemeContextLoader = ({ children }: { children: React.ReactNode }) => {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const [theme, setTheme] = useState<'light' | 'dark'>(prefersDarkMode ? 'dark' : 'light');
    const [spacing, setSpacing] = useState<'compact' | 'comfortable' | 'spacious'>('comfortable');
    const [scaleSize, setScaleSize] = useState<number>(() => {
        try {
            const stored = localStorage.getItem(APP_SETTINGS);
            if (stored) {
                const settings = JSON.parse(stored) as Partial<LocalAppSettings>;
                if (settings.uiScaleSize) return settings.uiScaleSize;
            }
        } catch {
            // ignore
        }
        return 16;
    });
    const [panelButtonScaleSize, setPanelButtonScaleSize] = useState<number>(() => {
        try {
            const stored = localStorage.getItem(APP_SETTINGS);
            if (stored) {
                const settings = JSON.parse(stored) as Partial<LocalAppSettings>;
                if (settings.panelButtonScaleSize) return settings.panelButtonScaleSize;
            }
        } catch {
            // ignore
        }
        return 16;
    });
    const [panelPosition, setPanelPosition] = useState<'left' | 'right' | 'top' | 'bottom'>('left'); // Default

    useEffect(() => {
        setTheme(prefersDarkMode ? 'dark' : 'light');
    }, [prefersDarkMode]);

    const muiThemeObject = useMemo(() => (theme === 'light' ? muiLightTheme : muiDarkTheme), [theme]);
    const emotionThemeObject = useMemo(
        () => ({
            ...(theme === 'light' ? emotionLightTheme : emotionDarkTheme),
            mode: theme,
            spacing,
            scaleSize,
            panelButtonScaleSize,
            panelPosition,
            panelOrientation: (panelPosition === 'left' || panelPosition === 'right'
                ? 'vertical'
                : 'horizontal') as PanelOrientation,
        }),
        [theme, spacing, scaleSize, panelButtonScaleSize, panelPosition]
    );

    const setThemeDirectly = useCallback((theme: 'light' | 'dark') => {
        setTheme(theme);
    }, []);

    const contextValue = useMemo(
        () => ({
            theme,
            setThemeDirectly,
            spacing,
            setSpacing,
            scaleSize,
            setScaleSize,
            panelButtonScaleSize,
            setPanelButtonScaleSize,
            panelPosition,
            setPanelPosition,
        }),
        [
            theme,
            setThemeDirectly,
            spacing,
            setSpacing,
            scaleSize,
            setScaleSize,
            panelButtonScaleSize,
            setPanelButtonScaleSize,
            panelPosition,
            setPanelPosition,
        ]
    );

    return (
        <IconContext.Provider
            value={{
                weight: 'regular',
            }}>
            <ThemeContext.Provider value={contextValue}>
                <EmotionThemeProvider theme={emotionThemeObject}>
                    <GlobalStyles />
                    <MuiThemeProvider theme={muiThemeObject}>{children}</MuiThemeProvider>
                </EmotionThemeProvider>
            </ThemeContext.Provider>
        </IconContext.Provider>
    );
};
